// EndpointResources — bottom-of-endpoint horizontal "share & export" strip.
// Shows 4 quick actions per endpoint: Permalink (copy current URL),
// Raw JSON (OpenAPI spec in new tab w/ fragment hint), YAML (download),
// and "Lapor masalah dokumentasi" (mailto with prefilled subject/body).
//
// Reuses the design tokens used elsewhere in the playground (dark theme,
// pill-shaped ghost buttons with hover-ink colour, hairline borders).

import { Link2, FileJson, FileText, AlertCircle, ExternalLink } from 'lucide-react';
import { showToast } from '@/lib/toast';

const API_BASE = 'https://api.satutim.id';

interface Props {
    operationId: string;
    method: string;
    path: string;
}

export default function EndpointResources({ operationId, method, path }: Props) {
    const methodLower = method.toLowerCase();
    // OpenAPI JSON fragment hint — some viewers (Stoplight/Swagger UI variants)
    // honour `#/paths/<encoded path>/<method>` to scroll to the operation.
    const rawJsonHref = `${API_BASE}/openapi.json#/paths/${encodeURIComponent(path)}/${methodLower}`;
    const yamlHref = `${API_BASE}/openapi.yaml`;

    const mailtoHref = (() => {
        const subject = `Doc issue ${operationId}`;
        const body = `Operation ${operationId} (${method} ${path})\n\nDeskripsi masalah:\n\n\n---\nHalaman: ${typeof window !== 'undefined' ? window.location.href : ''}`;
        return `mailto:support@satutim.id?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    })();

    const handlePermalink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            showToast('Permalink tersalin');
        } catch {
            showToast('Gagal menyalin permalink');
        }
    };

    return (
        <div
            className="mt-10 pt-5 flex items-center flex-wrap gap-2"
            style={{ borderTop: '1px solid var(--border)' }}
            aria-label="Sumber & pelaporan endpoint"
        >
            <span className="text-xxs text-ghost uppercase tracking-wider font-semibold mr-1">
                Bagikan & ekspor
            </span>

            <button
                type="button"
                onClick={handlePermalink}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xxs text-muted hover:text-ink transition-colors"
                style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                aria-label="Salin permalink halaman ini"
            >
                <Link2 className="h-3 w-3" />
                Permalink
            </button>

            <a
                href={rawJsonHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xxs text-muted hover:text-ink transition-colors"
                style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                aria-label="Buka OpenAPI JSON di tab baru"
            >
                <FileJson className="h-3 w-3" />
                Raw JSON
                <ExternalLink className="h-2.5 w-2.5 opacity-70" />
            </a>

            <a
                href={yamlHref}
                target="_blank"
                rel="noopener noreferrer"
                download=""
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xxs text-muted hover:text-ink transition-colors"
                style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                aria-label="Unduh OpenAPI YAML"
            >
                <FileText className="h-3 w-3" />
                YAML
            </a>

            <a
                href={mailtoHref}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xxs text-muted hover:text-ink transition-colors ml-auto"
                style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                aria-label="Lapor masalah dokumentasi via email"
            >
                <AlertCircle className="h-3 w-3" />
                Lapor masalah dokumentasi
            </a>
        </div>
    );
}
